import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API } from "../lib/api";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [adminToken, setAdminToken] = useState(() => localStorage.getItem("argus_admin_token") || "");
  const [guestToken, setGuestToken] = useState(() => sessionStorage.getItem("argus_guest_token") || "");
  const [guestSessionId, setGuestSessionId] = useState(() => sessionStorage.getItem("argus_guest_session") || "");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isGuestMode, setIsGuestMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Get the active token (admin takes priority over guest)
  const token = adminToken || guestToken;

  // Verify token on mount and when it changes
  const verifyToken = useCallback(async () => {
    // Check admin token first
    if (adminToken) {
      try {
        await axios.get(`${API}/admin/dashboard`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        });
        setIsAuthenticated(true);
        setIsGuestMode(false);
        setIsLoading(false);
        return;
      } catch {
        // Admin token invalid - clear it
        setAdminToken("");
        localStorage.removeItem("argus_admin_token");
      }
    }

    // Check guest token
    if (guestToken) {
      try {
        // Verify guest token by checking if it's still valid (try to list SOPs)
        await axios.get(`${API}/sops`, {
          headers: { Authorization: `Bearer ${guestToken}` }
        });
        setIsAuthenticated(true);
        setIsGuestMode(true);
        setIsLoading(false);
        return;
      } catch {
        // Guest token invalid - clear it
        setGuestToken("");
        setGuestSessionId("");
        sessionStorage.removeItem("argus_guest_token");
        sessionStorage.removeItem("argus_guest_session");
      }
    }

    setIsAuthenticated(false);
    setIsGuestMode(false);
    setIsLoading(false);
  }, [adminToken, guestToken]);

  useEffect(() => {
    verifyToken();
  }, [verifyToken]);

  const login = async (password) => {
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      const newToken = res.data.token;
      setAdminToken(newToken);
      localStorage.setItem("argus_admin_token", newToken);
      setIsAuthenticated(true);
      setIsGuestMode(false);
      return { success: true };
    } catch (err) {
      return { 
        success: false, 
        error: err.response?.data?.detail || "Login failed" 
      };
    }
  };

  // Start a guest sandbox session
  const startGuestSession = async (email = null, name = null, fromChatSession = null) => {
    try {
      const res = await axios.post(`${API}/sandbox/init`, {
        email,
        name,
        from_chat_session: fromChatSession
      });
      
      const { guest_token, session_id } = res.data;
      
      setGuestToken(guest_token);
      setGuestSessionId(session_id);
      sessionStorage.setItem("argus_guest_token", guest_token);
      sessionStorage.setItem("argus_guest_session", session_id);
      setIsAuthenticated(true);
      setIsGuestMode(true);
      
      return { success: true, session_id, guest_token };
    } catch (err) {
      return {
        success: false,
        error: err.response?.data?.detail || "Failed to start sandbox"
      };
    }
  };

  // Convert guest session to full member
  const convertToMember = async (name, email, restaurantName = null) => {
    if (!guestSessionId) {
      return { success: false, error: "No active guest session" };
    }
    
    try {
      const res = await axios.post(`${API}/sandbox/convert`, null, {
        params: {
          session_id: guestSessionId,
          name,
          email,
          restaurant_name: restaurantName
        }
      });
      
      // Clear guest session after successful conversion
      endGuestSession();
      
      return {
        success: true,
        memberNumber: res.data.member_number,
        remainingSpots: res.data.remaining_spots
      };
    } catch (err) {
      return {
        success: false,
        error: err.response?.data?.detail || "Failed to convert to member"
      };
    }
  };

  const endGuestSession = () => {
    setGuestToken("");
    setGuestSessionId("");
    sessionStorage.removeItem("argus_guest_token");
    sessionStorage.removeItem("argus_guest_session");
    if (!adminToken) {
      setIsAuthenticated(false);
      setIsGuestMode(false);
    }
  };

  const logout = () => {
    setAdminToken("");
    setGuestToken("");
    setGuestSessionId("");
    localStorage.removeItem("argus_admin_token");
    sessionStorage.removeItem("argus_guest_token");
    sessionStorage.removeItem("argus_guest_session");
    setIsAuthenticated(false);
    setIsGuestMode(false);
  };

  // Axios interceptor to add auth header
  useEffect(() => {
    const interceptor = axios.interceptors.request.use((config) => {
      const activeToken = adminToken || guestToken;
      if (activeToken && config.url?.includes("/api/")) {
        config.headers.Authorization = `Bearer ${activeToken}`;
      }
      return config;
    });

    return () => axios.interceptors.request.eject(interceptor);
  }, [adminToken, guestToken]);

  // Axios interceptor to handle 401 errors — Force Logout
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          if (adminToken) {
            // Admin token is invalid/expired — force full logout and redirect to login
            logout();
            // Use window.location to ensure a clean page reload clears any stale state
            const isAdminRoute = window.location.pathname.startsWith("/admin") &&
              window.location.pathname !== "/admin/login";
            if (isAdminRoute) {
              window.location.href = "/admin/login?session=expired";
            }
          } else if (guestToken) {
            endGuestSession();
          }
        }
        return Promise.reject(error);
      }
    );

    return () => axios.interceptors.response.eject(interceptor);
  }, [adminToken, guestToken]);

  return (
    <AuthContext.Provider value={{ 
      token,
      adminToken,
      guestToken,
      guestSessionId,
      isAuthenticated, 
      isGuestMode,
      isLoading, 
      login, 
      logout,
      startGuestSession,
      endGuestSession,
      convertToMember
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component - allows both admin and guest
export const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { isAuthenticated, isGuestMode, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      // Redirect to login with return URL
      navigate(`/admin/login?redirect=${encodeURIComponent(location.pathname)}`, { replace: true });
    } else if (!isLoading && adminOnly && isGuestMode) {
      // Guest trying to access admin-only route
      navigate("/", { replace: true });
    }
  }, [isAuthenticated, isGuestMode, isLoading, navigate, location, adminOnly]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#040F49] flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400 text-sm">Verifying authentication...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null; // Will redirect via useEffect
  }

  if (adminOnly && isGuestMode) {
    return null; // Will redirect via useEffect
  }

  return children;
};

// Guest-Allowed Route - allows guest OR admin (for SOP Builder)
export const GuestAllowedRoute = ({ children }) => {
  return <ProtectedRoute adminOnly={false}>{children}</ProtectedRoute>;
};

// Admin-Only Route - requires admin token (for admin dashboard)
export const AdminOnlyRoute = ({ children }) => {
  return <ProtectedRoute adminOnly={true}>{children}</ProtectedRoute>;
};

export default AuthContext;
